<script lang="ts" setup>
import Views from '@/components/Views/Views.vue';
import ButtonBack from '@/components/Button/ButtonBack.vue';
import { computed, ref, onMounted, Component } from 'vue';
import CampaignsCardType from '@/components/Campaigns/CampaignsCard/CampaignsCardType.vue';
import BcButton from '@/ui-kit/components/BcButton/BcButton.vue';
import { capitalize } from '@/utils/stringTransform';
import BcDropdownItem from '@/ui-kit/components/BcDropdown/BcDropdownItem.vue';
import BcDropdown from '@/ui-kit/components/BcDropdown/BcDropdown.vue';
import { useI18n } from '@/i18n/i18n';
import { addDays, addMonths, addWeeks, format } from 'date-fns';
import ProspectsTabs from '@/views/Tabs/ProspectsTabs.vue';
import ProspectsFilters from '@/components/Prospects/ProspectsFilters.vue';
import ProspectsStatusTabs from '@/components/Prospects/ProspectsStatusTabs.vue';
import ProspectsGrid from '@/components/Prospects/ProspectsGrid.vue';
import ProspectsCardsList from '@/components/Prospects/ProspectsCardsList.vue';
import { useRouter } from 'vue-router/composables';
import { useQuery, useInfiniteQuery } from '@tanstack/vue-query';
import { getCampaignProspects, getCampaign, archiveCampaign, deleteCampaign } from '@/api/campaigns';
import { useRoute } from 'vue-router/composables';
import { startCampaign, resumeCampaign, pauseCampaign, scheduleCampaign, duplicateCampaign } from '@/api/campaigns';
import { usePanelStore } from '@/store/pinia/panel';
import CampaignsCreateView from '@/components/Campaigns/CampaignsCreate/CampaignsCreateView.vue';
import { campaignActionKeys, type Campaign, type CampaignProspect, type CampaignStatusKey } from '@/domains/models/Campaign';
import CampaignsCardStatus from '@/components/Campaigns/CampaignsCard/CampaignsCardStatus.vue';
import store from '@/store';
import { useMultiselect } from '@/composables/useMultiselect';
import CampaignsMultiselectFooter from '@/components/Campaigns/CampaignsMultiselectFooter.vue';
import ProspectsMultiselectFooter from '@/components/Prospects/ProspectsMultiselectFooter.vue';
import ArchiveIcon from '@/ui-kit/assets/icons/archive.svg?component';
import { useUnipileAccountsStore } from '@/store/pinia/unipileAccounts';
import { UnipileStatus } from '@/domains/UnipileAccount';

const route = useRoute();
const panelStore = usePanelStore();

const campaignId = computed(() => route.query['campaignId'] as string | undefined);
const currentActionId = computed(() => route.query['currentActionId'] as string | undefined);

const prospectsRouteQueries = computed(() => ({
  ...campaignId.value && { campaignId: campaignId.value },
  ...currentActionId.value && { currentActionId: currentActionId.value },
}));

interface ProspectsPage {
  items: CampaignProspect[];
  cursor?: string;
}

interface InfiniteProspectsData {
  pages: ProspectsPage[];
  pageParams: (string | undefined)[];
}

const { selectedCards, setSelectedCards, resetSelectedCards, getIsSelectedStatus } = useMultiselect();

// Add typed handler for card selection
function handleCardSelection(card: CampaignProspect, value: boolean) {
  setSelectedCards(value, card);
}

// Reset both cards and selected cards before starting the query
store.dispatch('resetCards');
resetSelectedCards();

const {
  data: prospectsPages,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery<ProspectsPage, Error, InfiniteProspectsData>({
  queryKey: ['prospects', prospectsRouteQueries],
  queryFn: async ({ pageParam }) => {
    const response = await getCampaignProspects({
      ...prospectsRouteQueries.value,
      cursor: pageParam as string | undefined,
    });
    store.dispatch('setCards', response.data.items);
    return response.data;
  },
  getNextPageParam: (lastPage) => lastPage.cursor ?? null,
  initialPageParam: undefined,
});

const prospects = computed(() =>
  prospectsPages.value?.pages?.flatMap((page: ProspectsPage) => page.items) ?? []
);

const campaign = ref<Campaign>();

const { data: initialCampaign } = useQuery({
  queryKey: ['campaign', campaignId],
  queryFn: async () => {
    if (!campaignId.value) {
      return null;
    }
    const response = await getCampaign(campaignId.value);
    campaign.value = response.data;
    return response.data;
  },
});

const { t } = useI18n();
const router = useRouter();

const dateOptions = computed<{
  name: () => string;
  date: Date | string;
}[]>(() => [
  {
    name: t('companies.panel-view-timeline.none'),
    date: t('companies.panel-view-timeline.none-min'),
  },
  {
    name: `${t('hunt.in-two-days')} (${format(addDays(new Date(), 2), 'P')})`,
    date: addDays(new Date(), 2),
  },
  {
    name: `${t('hunt.in-one-week')} (${format(addWeeks(new Date(), 1), 'P')})`,
    date: addWeeks(new Date(), 1),
  },
  {
    name: `${t('hunt.in-three-months')} (${format(addMonths(new Date(), 3), 'P')})`,
    date: addMonths(new Date(), 3),
  },
  {
    name: `${t('hunt.in-six-months')} (${format(addMonths(new Date(), 6), 'P')})`,
    date: addMonths(new Date(), 6),
  },
  {
    name: `${t('hunt.in-nine-months')} (${format(addMonths(new Date(), 9), 'P')})`,
    date: addMonths(new Date(), 9),
  },
]);

// Helper function to check campaign status conditions
const isArchived = computed<boolean>(() => campaign.value?.status === 'ARCHIVED');
const isDraft = computed<boolean>(() => campaign.value?.status === 'DRAFT');
const isOngoing = computed<boolean>(() => campaign.value?.status === 'ONGOING');
const isPaused = computed<boolean>(() => campaign.value?.status === 'PAUSED');
const hasSchedule = computed<boolean>(() => !!campaign.value?.scheduledAt);

const settingsOptions = computed<{
  key: string;
  name: string | (() => string);
  icon: string;
  svgComponent?: Component;
  color?: string;
}[]>(() => {
  const options = [];

  // Edit is always accessible
  options.push({
    key: 'edit',
    name: t('generics.edit'),
    icon: 'edit-3',
  });

  // Duplicate is always available
  options.push({
    key: 'duplicate',
    name: t('generics.duplicate'),
    icon: 'file-copy',
  });

  // Archive is available for all statuses except ARCHIVED
  if (!isArchived.value) {
    options.push({
      key: 'archive',
      name: capitalize(t('generics.to-archive')),
      icon: '',
      svgComponent: ArchiveIcon,
    });
  }

  // Delete is allowed only for DRAFT and ARCHIVED
  if (isDraft.value || isArchived.value) {
    options.push({
      key: 'delete',
      name: t('generics.delete'),
      icon: 'trash',
      color: 'danger',
    });
  }

  return options;
});

function goToCampaigns() {
  router.push({ name: 'Campaigns' });
}

function handleOptionClick(option: string) {
  // Handle matrix of allowed actions based on campaign status
  if (option === 'edit') {
    // Edit is always accessible
    handleEdit();
  } else if (option === 'duplicate') {
    // Duplicate is always available
    handleDuplicateCampaign();
  } else if (option === 'archive' && !isArchived.value) {
    // Archive is only available if not archived
    handleArchiveCampaign();
  } else if (option === 'delete' && (isDraft.value || isArchived.value)) {
    // Delete handling would go here for DRAFT and ARCHIVED only
    handleDeleteCampaign();
  }
}

function handleEdit() {
  panelStore.openModal(CampaignsCreateView, {
    campaign: campaign.value ?? {},
  });
}

async function handleStartCampaign() {
  if (!campaignId.value) return;
  const response = await startCampaign(campaignId.value);
  campaign.value = { ...campaign.value, ...response.data };
}

async function handleResumeCampaign() {
  if (!campaignId.value) return;
  const response = await resumeCampaign(campaignId.value);
  campaign.value = { ...campaign.value, ...response.data };
}

async function handlePauseCampaign() {
  if (!campaignId.value) return;
  const response = await pauseCampaign(campaignId.value);
  campaign.value = { ...campaign.value, ...response.data };
}

async function handleArchiveCampaign() {
  if (!campaignId.value) return;
  const response = await archiveCampaign(campaignId.value);
  campaign.value = { ...campaign.value, ...response.data };
}

async function handleScheduleCampaign(scheduledAt: Date) {
  if (!campaignId.value) return;
  const response = await scheduleCampaign(campaignId.value, scheduledAt);
  campaign.value = { ...campaign.value, ...response.data };
}

async function handleDuplicateCampaign() {
  if (!campaignId.value) return;
  const response = await duplicateCampaign(campaignId.value);
  campaign.value = response.data;
  router.push({ name: 'Prospects', query: { campaignId: response.data._id } });
}

function handleDateOptionClick(option: { date: Date | string }) {
  if (option.date instanceof Date) {
    handleScheduleCampaign(option.date);
  }
}

async function handleDeleteCampaign() {
  if (!campaignId.value) return;
  await deleteCampaign(campaignId.value);
  router.push({ name: 'Campaigns' });
}

// Get LinkedIn account status
const unipileAccountsStore = useUnipileAccountsStore();

// Check if LinkedIn account is valid (OK or SYNC_SUCCESS)
const isAccountValid = computed(() => {
  const linkedInAccount = unipileAccountsStore.linkedInAccount;
  return linkedInAccount &&
    (linkedInAccount.status === UnipileStatus.OK ||
      linkedInAccount.status === UnipileStatus.SYNC_SUCCESS);
});

// Check for missing prospects
const hasMissingProspects = computed(() => !campaign.value?._prospectsSources?.length);

// Check for missing actions
const hasMissingActions = computed(() => !campaign.value?._actions?.length);

const areMessagesInCampaignActionsValid = computed(() => {
  if (!campaign.value?.actions?.length) return true; // No actions means no validation needed
  
  return campaign.value.actions.every((action) => {
    // For LinkedIn message, check only body
    if (action.type === campaignActionKeys.LINKEDIN_MESSAGE) {
      return action?.body?.length > 0;
    }
    
    // For InMail types, check both subject and body
    if ([campaignActionKeys.LINKEDIN_INMAIL_PREMIUM, campaignActionKeys.LINKEDIN_INMAIL_RECRUITER, campaignActionKeys.LINKEDIN_INMAIL_SALES_NAVIGATOR].includes(action.type)) {
      return action?.subject?.length > 0 && action?.body?.length > 0;
    }
    
    // For other action types, no validation needed
    return true;
  });
});

// Validation for start and resume buttons
const campaignValidationErrors = computed(() => {
  const errors = [];

  if (hasMissingProspects.value) {
    errors.push(t('campaigns.errors.missing-prospects'));
  }

  if (hasMissingActions.value) {
    errors.push(t('campaigns.errors.missing-actions'));
  }

  if (!isAccountValid.value) {
    errors.push(t('campaigns.errors.invalid-account'));
  }

  if (!areMessagesInCampaignActionsValid.value) {
    errors.push(t('campaigns.errors.empty-messages'));
  }

  return errors;
});

const canStartOrResumeOrSchedule = computed(() => {
  return campaignValidationErrors.value.length === 0;
});

// Generate tooltip text from validation errors
const validationTooltip = computed(() => {
  if (campaignValidationErrors.value.length === 0) return '';
  return campaignValidationErrors.value.join('<br/>');
});

</script>

<template>
  <views class="database-campaigns flex w-full shrink-0 flex-col !items-start overflow-auto">
    <div
      class="flex w-full min-w-[1180px] items-center justify-between border-b border-b-blue-200 bg-neutral-200 px-[30px] py-2.5">
      <div class="flex gap-5">
        <ButtonBack @click="goToCampaigns">
          <p class="first-letter:capitalize">
            {{ $t('generics.back-to-resources', { resources: $tc('campaigns.to-campaigns', 2) }) }}
          </p>
        </ButtonBack>
        <div class="flex items-center gap-2.5">
          <h1 class="text-xl font-bold text-blue-800">{{ campaign?.name }}</h1>
          <CampaignsCardType v-if="campaign?.campaignTypes?.length" :type="campaign.campaignTypes[0].name" />
          <CampaignsCardStatus v-if="campaign" :campaign="campaign" />
        </div>
      </div>
      <div class="flex gap-2.5">
        <div v-if="isDraft || isPaused" v-tooltip="!canStartOrResumeOrSchedule ? validationTooltip : ''">
          <BcDropdown v-if="(isDraft || isPaused) && !isArchived" :options="dateOptions"
            :disabled="!canStartOrResumeOrSchedule" class="flex shrink-0" position="bottom-left">
            <template #label="{ isDropdownOpen }">
              <BcButton icon-left="calendar" class="h-9 !text-sm" type="outlined" @click.prevent>
                {{ hasSchedule ? capitalize($t('campaigns.reschedule')) : capitalize($t('generics.to-program')) }}
              </BcButton>
            </template>
            <template #option="{ activeIndex, index, option }">
              <BcDropdownItem :icon="option.icon" :is-focus="activeIndex === index" :is-selected="false"
                :text="option.name" :title="option.name" @click.native="handleDateOptionClick(option)">
              </BcDropdownItem>
            </template>
          </BcDropdown>
        </div>


        <!-- Start campaign button - for DRAFT -->
        <div v-if="isDraft && !isArchived" v-tooltip="!canStartOrResumeOrSchedule ? validationTooltip : ''">
          <BcButton class="h-9 !text-sm" color="success" :disabled="!canStartOrResumeOrSchedule" icon-left="send"
            size="extra-small" @click="handleStartCampaign">
            {{ hasSchedule ? capitalize($t('campaigns.start-now')) : capitalize($t('campaigns.start-my-campaign')) }}
          </BcButton>
        </div>

        <!-- Resume campaign button - for PAUSED -->
        <div v-if="isPaused && !isArchived" v-tooltip="!canStartOrResumeOrSchedule ? validationTooltip : ''">
          <BcButton class="h-9 !text-sm" color="success" :disabled="!canStartOrResumeOrSchedule" icon-left="send"
            size="extra-small" @click="handleResumeCampaign">
            {{ capitalize($t('campaigns.resume-my-campaign')) }}
          </BcButton>
        </div>

        <BcButton v-if="isOngoing && !isArchived" class="h-9 !text-sm" type="outlined" color="success"
          :disabled="!campaign?._prospectsSources?.length || !campaign?._actions?.length"
          :v-tooltip="!campaign?._prospectsSources?.length ? 'Vous devez ajouter des prospects' : 'Vous devez ajouter des actions'"
          icon-left="pause" size="extra-small" @click="handlePauseCampaign">
          {{ capitalize($t('campaigns.pause-my-campaign')) }}
        </BcButton>

        <BcDropdown :options="settingsOptions" position="bottom-left">
          <template #label="{ isDropdownOpen }">
            <BcButton icon-left="settings" size="small" @click.prevent>
            </BcButton>
          </template>
          <template #option="{ activeIndex, index, option }">
            <BcDropdownItem :class="option.class" :color="option.color" :icon="option.icon"
              :svg-component="option.svgComponent" :is-focus="activeIndex === index" :is-selected="false"
              :text="option.name" :title="option.name" @click.native="handleOptionClick(option.key)">
            </BcDropdownItem>
          </template>
        </BcDropdown>
      </div>
    </div>
    <div class="flex w-full min-w-[1180px]">
      <ProspectsTabs>
        <ProspectsFilters />
      </ProspectsTabs>
    </div>
    <RouterView />
    <div v-if="campaign?._actions?.length" class="flex w-full min-w-[1180px] flex-col px-[30px] py-[20px]">
      <ProspectsStatusTabs :campaign="campaign" class="mb-[20px]" />
      <ProspectsGrid />
      <ProspectsCardsList :cards="prospects" :has-more="hasNextPage" :is-loading-more="isFetchingNextPage"
        :selected-cards="selectedCards" :get-is-selected-status="getIsSelectedStatus" @load-more="fetchNextPage"
        @select-card="handleCardSelection" />
    </div>
    <ProspectsMultiselectFooter v-if="selectedCards.length"
      class="is-fixed is-bottom is-left is-full-width is-justify-content-center is-align-items-center is-column">
    </ProspectsMultiselectFooter>
  </views>
</template>

<style lang="scss" scoped>
.database-campaigns {
  @include page_grid();
}
</style>
